import type { Header } from "../replay/format";
import type { LivePlayback } from "../state/live";
import type { Playback } from "../state/playback";
import { $, clock, int, pct } from "./format";

export const SPEEDS = [0.25, 0.5, 1, 2, 4, 8, 16, 32, 64, 128];

/**
 * Transport controls and the timeline graph: forces per side (solid), share
 * of each side's units in contact that are out of ammunition (dashed, 0-100%
 * on the same height), and deaths per frame (bars along the bottom).
 */
export class Timeline {
  private canvas = $("graph") as HTMLCanvasElement;
  private tip = $("graph-tip");
  private play = $("play-btn") as HTMLButtonElement;
  private speed = $("speed") as HTMLSelectElement;
  private clockEl = $("clock");
  private buffering = $("buffering");
  private colors: string[];
  private maxAlive: number;
  private maxDeaths: number;
  private dragging = false;
  private pending: number | null = null;
  private image: HTMLCanvasElement | null = null;
  private drawnWidth = 0;
  /** Frames the cached graph covers (a live stream keeps growing). */
  private drawnFrames = 0;

  constructor(
    private playback: Playback | LivePlayback,
    private header: Header,
  ) {
    this.colors = header.factions.map((f) => f.color);
    const s = header.summary;
    this.maxAlive = Math.max(1, ...s.alive.map((a) => Math.max(...a)));
    this.maxDeaths = Math.max(1, ...s.deaths.map((d) => d.reduce((x, y) => x + y, 0)));

    this.speed.replaceChildren(
      ...SPEEDS.map((v) => {
        const o = document.createElement("option");
        o.value = String(v);
        o.textContent = `${v}×`;
        return o;
      }),
    );
    this.speed.value = String(playback.speed);
    this.speed.addEventListener("change", () => (playback.speed = Number(this.speed.value)));

    this.play.addEventListener("click", () => this.toggle());
    $("start-btn").addEventListener("click", () => void playback.seek(0));
    $("back-btn").addEventListener("click", () => void playback.step(-1));
    $("fwd-btn").addEventListener("click", () => void playback.step(1));

    const frameAt = (e: PointerEvent) => {
      const r = this.canvas.getBoundingClientRect();
      return Math.round(((e.clientX - r.left) / r.width) * (header.frames - 1));
    };
    this.canvas.addEventListener("pointerdown", (e) => {
      this.dragging = true;
      this.canvas.setPointerCapture(e.pointerId);
      this.seek(frameAt(e));
    });
    this.canvas.addEventListener("pointermove", (e) => {
      if (this.dragging) this.seek(frameAt(e));
      this.showTip(frameAt(e), e);
    });
    this.canvas.addEventListener("pointerup", () => (this.dragging = false));
    this.canvas.addEventListener("pointerleave", () => this.tip.classList.add("hidden"));
  }

  toggle(): void {
    const p = this.playback;
    if (!p.playing && p.state.frame >= p.frames - 1) void p.seek(0);
    p.playing = !p.playing;
  }

  faster(dir: number): void {
    const i = SPEEDS.indexOf(this.playback.speed);
    const next = SPEEDS[Math.max(0, Math.min(SPEEDS.length - 1, (i < 0 ? 5 : i) + dir))];
    this.playback.speed = next;
    this.speed.value = String(next);
  }

  /** Seeks, coalescing rapid requests (dragging) into one in flight. */
  private seek(frame: number): void {
    if (this.pending !== null) {
      this.pending = frame;
      return;
    }
    this.pending = frame;
    const run = async () => {
      while (this.pending !== null) {
        const f = this.pending;
        await this.playback.seek(f);
        if (this.pending === f) this.pending = null;
      }
    };
    void run();
  }

  private showTip(frame: number, e: PointerEvent): void {
    const s = this.header.summary;
    if (frame < 0 || frame >= this.header.frames) return;
    const t = (frame * this.header.frame_interval) / this.header.tick_rate;
    const lines = this.header.factions.map((f, i) => {
      const alive = s.alive[frame]?.[i] ?? 0;
      const contact = s.in_contact[frame]?.[i] ?? 0;
      const dry = s.dry_in_contact[frame]?.[i] ?? 0;
      return `<span style="color:${f.color}">■</span> ${f.name}: ${int(alive)} · in contact ${int(contact)} · dry ${pct(dry, contact)}`;
    });
    this.tip.innerHTML = `<b>${clock(t)}</b><br>${lines.join("<br>")}`;
    this.tip.classList.remove("hidden");
    const r = this.canvas.getBoundingClientRect();
    const x = Math.min(r.width - this.tip.offsetWidth, Math.max(0, e.clientX - r.left - this.tip.offsetWidth / 2));
    this.tip.style.left = `${x}px`;
  }

  /** Redraws the playhead and readouts (the graph itself is cached). */
  update(): void {
    const p = this.playback;
    this.play.textContent = p.playing ? "⏸" : "▶︎";
    this.play.setAttribute("aria-label", p.playing ? "Pause" : "Play");
    const t = (p.time * this.header.frame_interval) / this.header.tick_rate;
    const total = ((this.header.frames - 1) * this.header.frame_interval) / this.header.tick_rate;
    const end = "source" in p.replay ? clock(total) : "live";
    this.clockEl.innerHTML = `${clock(t)} <span class="muted">/ ${end}</span><small>tick ${int(p.state.tick)}</small>`;
    const arrived = p.replay.framesArrived();
    const progress = "source" in p.replay ? p.replay.source.progress : 1;
    this.buffering.textContent = p.buffering ? "buffering…" : `downloading ${Math.round(progress * 100)}%`;
    this.buffering.classList.toggle("hidden", !p.buffering && arrived >= this.header.frames);
    this.draw();
  }

  private draw(): void {
    const c = this.canvas;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round(c.clientWidth * dpr);
    const h = Math.round(c.clientHeight * dpr);
    if (w === 0 || h === 0) return;
    const g = c.getContext("2d")!;
    // Live: redraw as frames arrive (every few, not every display frame).
    const grown = this.header.frames >= this.drawnFrames + 4;
    if (c.width !== w || c.height !== h || this.drawnWidth !== w || !this.image || grown) {
      this.drawnFrames = this.header.frames;
      const s = this.header.summary;
      this.maxAlive = Math.max(1, ...s.alive.map((a) => Math.max(...a)));
      c.width = w;
      c.height = h;
      // The graph only changes with size: draw it once, offscreen.
      const img = document.createElement("canvas");
      img.width = w;
      img.height = h;
      this.drawGraph(img.getContext("2d")!, w, h);
      this.image = img;
      this.drawnWidth = w;
    }
    g.clearRect(0, 0, w, h);
    g.drawImage(this.image, 0, 0);
    const n = this.header.frames;
    // Not downloaded yet.
    const arrived = this.playback.replay.framesArrived();
    if (arrived < n) {
      const ax = (arrived / Math.max(1, n - 1)) * w;
      g.fillStyle = "rgba(7,8,10,0.6)";
      g.fillRect(ax, 0, w - ax, h);
      g.fillStyle = "rgba(242,169,59,0.8)";
      g.fillRect(0, h - 2, ax, 2);
    }
    const x = (this.playback.time / Math.max(1, n - 1)) * w;
    g.fillStyle = "rgba(255,255,255,0.08)";
    g.fillRect(0, 0, x, h);
    g.fillStyle = "#fff";
    g.fillRect(Math.round(x) - 1, 0, 2 * dpr, h);
  }

  private drawGraph(g: CanvasRenderingContext2D, w: number, h: number): void {
    const s = this.header.summary;
    const n = this.header.frames;
    const dpr = w / Math.max(1, this.canvas.clientWidth);
    g.clearRect(0, 0, w, h);
    g.fillStyle = "rgba(255,255,255,0.03)";
    g.fillRect(0, 0, w, h);
    const X = (i: number) => (i / Math.max(1, n - 1)) * w;
    const top = 4 * dpr;
    const usable = h - top - 2 * dpr;

    // Minute ticks, labelled every few minutes so labels never crowd.
    const fps = this.header.tick_rate / this.header.frame_interval;
    const minutes = n / (60 * fps);
    const every = [1, 2, 5, 10, 15, 30, 60].find((k) => w / (minutes / k) > 70 * dpr) ?? 60;
    g.font = `${10 * dpr}px ui-monospace, Consolas, monospace`;
    g.textBaseline = "top";
    for (let m = 1; m * 60 * fps < n; m++) {
      const x = Math.round(X(m * 60 * fps));
      g.fillStyle = "rgba(255,255,255,0.07)";
      g.fillRect(x, 0, 1, h);
      if (m % every === 0) {
        g.fillStyle = "rgba(255,255,255,0.45)";
        g.fillText(`${m}m`, x + 3 * dpr, 2 * dpr);
      }
    }

    // Deaths per frame.
    const bucket = Math.max(1, Math.floor(n / w));
    g.fillStyle = "rgba(239,91,91,0.55)";
    for (let i = 0; i < n; i += bucket) {
      let d = 0;
      for (let k = i; k < Math.min(n, i + bucket); k++) d += (s.deaths[k] ?? []).reduce((a, b) => a + b, 0);
      d /= bucket;
      const bh = Math.sqrt(d / this.maxDeaths) * usable * 0.45;
      g.fillRect(X(i), h - bh, Math.max(1, X(bucket) - X(0)), bh);
    }

    this.header.factions.forEach((_, f) => {
      // Forces.
      g.strokeStyle = this.colors[f];
      g.lineWidth = 1.6 * dpr;
      g.setLineDash([]);
      g.beginPath();
      for (let i = 0; i < n; i += bucket) {
        const y = top + usable * (1 - (s.alive[i]?.[f] ?? 0) / this.maxAlive);
        if (i === 0) g.moveTo(X(i), y);
        else g.lineTo(X(i), y);
      }
      g.stroke();
      // Out of ammunition in contact (share).
      g.globalAlpha = 0.65;
      g.lineWidth = 1 * dpr;
      g.setLineDash([3 * dpr, 3 * dpr]);
      g.beginPath();
      let started = false;
      for (let i = 0; i < n; i += bucket) {
        const contact = s.in_contact[i]?.[f] ?? 0;
        if (contact < 20) {
          started = false;
          continue;
        }
        const y = top + usable * (1 - (s.dry_in_contact[i]?.[f] ?? 0) / contact);
        if (!started) g.moveTo(X(i), y);
        else g.lineTo(X(i), y);
        started = true;
      }
      g.stroke();
      g.globalAlpha = 1;
      g.setLineDash([]);
    });

    // Legend, top right.
    const items: [string, (x: number, y: number) => void][] = [
      ["units alive", (x, y) => g.fillRect(x, y, 12 * dpr, 2 * dpr)],
      ["dry in contact", (x, y) => {
        for (let k = 0; k < 3; k++) g.fillRect(x + k * 5 * dpr, y, 3 * dpr, 1.5 * dpr);
      }],
      ["deaths", (x, y) => {
        g.fillStyle = "rgba(239,91,91,0.8)";
        g.fillRect(x + 4 * dpr, y - 4 * dpr, 4 * dpr, 7 * dpr);
      }],
    ];
    g.font = `${10 * dpr}px ui-sans-serif, system-ui, sans-serif`;
    g.textBaseline = "middle";
    const gap = 8 * dpr;
    const markW = 16 * dpr;
    const widths = items.map(([label]) => g.measureText(label).width);
    const total = widths.reduce((a, b) => a + b, 0) + items.length * (markW + 4 * dpr) + (items.length - 1) * gap;
    let x = w - total - 8 * dpr;
    const y = 9 * dpr;
    // A backing so minute labels and lines never run through it.
    g.fillStyle = "rgba(17,20,26,0.92)";
    g.fillRect(x - 6 * dpr, 0, total + 12 * dpr, 18 * dpr);
    items.forEach(([label, mark], i) => {
      g.fillStyle = "rgba(255,255,255,0.75)";
      mark(x, y);
      x += markW + 4 * dpr;
      g.fillStyle = "rgba(255,255,255,0.6)";
      g.fillText(label, x, y);
      x += widths[i] + gap;
    });
  }
}
